import React from 'react';
import {
  EllipsisVertical,
  LogIn,
  LogOut,
  Settings,
  HelpCircle,
  Star,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';

const noop = () => {};

const MailtoUpgrade = () => {
  if (typeof window !== 'undefined') {
    window.open('mailto:help@noderiver.ai?subject=Upgrade%20my%20BrandOn%20plan', '_blank');
  }
};

const MailtoSupport = () => {
  if (typeof window !== 'undefined') {
    window.open('mailto:help@noderiver.ai?subject=BrandOn%20support', '_blank');
  }
};

const AccountMenu = ({
  collapsed,
  authStatus,
  user,
  onLogin = noop,
  onLogout = noop,
  onOpenSettings = noop,
  accountLabel,
  brandVisualTheme,
}) => {
  const { resolvedTheme } = useTheme();
  const isBrandTheme = resolvedTheme === 'noderiver';
  const isDark = resolvedTheme === 'dark';

  const computedStatus = authStatus ?? (user ? 'authenticated' : 'unauthenticated');
  const isAuthenticated = computedStatus === 'authenticated';
  const initials =
    user?.name?.split(' ')
      .map((part) => part[0])
      .join('')
        .slice(0, 2)
      .toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    'NR';

  const displayName = user?.name || user?.email || 'Guest user';
  const displayAccount = accountLabel || (isAuthenticated ? 'Personal account' : 'Guest mode');
  const primaryActionLabel = isAuthenticated ? 'Log out' : 'Sign in';
  const primaryActionIcon = isAuthenticated ? LogOut : LogIn;

  const PrimaryIcon = primaryActionIcon;

  const handlePrimary = () => {
    if (isAuthenticated) {
      onLogout();
    } else {
      onLogin();
    }
  };

  const accentGradient = brandVisualTheme?.gradients?.cta ?? 'linear-gradient(135deg, #1E8997, #0e273a)';
  const accentTextColor = brandVisualTheme?.palette?.textOnPrimary ?? '#ffffff';

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger
          className={cn(
          'rounded-full border p-1 focus:outline-none focus-visible:ring-2',
          isBrandTheme 
            ? 'border-white/15 bg-white/5 text-white hover:bg-white/10 focus-visible:ring-white/40'
            : (isDark 
              ? 'border-gray-700 bg-gray-800 text-white hover:bg-gray-700 focus-visible:ring-gray-500'
              : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-50 focus-visible:ring-slate-400'),
          collapsed && 'h-10 w-10 flex items-center justify-center'
        )}
      >
        <EllipsisVertical className="w-4 h-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className={cn("w-64", isBrandTheme || isDark ? "border-white/10 bg-gray-900 text-white" : "border-slate-200 bg-white text-slate-900")}>
        <DropdownMenuLabel>
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wide text-gray-400">Signed in as</p>
            <p className={cn("text-sm font-semibold truncate", isBrandTheme || isDark ? "text-white" : "text-slate-900")}>{displayName}</p>
            <p className="text-xs text-gray-500">{displayAccount}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={isBrandTheme || isDark ? "bg-white/10" : "bg-slate-100"} />
        <DropdownMenuItem onClick={MailtoUpgrade} className={cn("cursor-pointer", isBrandTheme || isDark ? "focus:bg-white/10 focus:text-white" : "focus:bg-slate-100 focus:text-slate-900")}>
          <Star className="w-4 h-4 mr-2" />
          Upgrade plan
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenSettings} className={cn("cursor-pointer", isBrandTheme || isDark ? "focus:bg-white/10 focus:text-white" : "focus:bg-slate-100 focus:text-slate-900")}>
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={MailtoSupport} className={cn("cursor-pointer", isBrandTheme || isDark ? "focus:bg-white/10 focus:text-white" : "focus:bg-slate-100 focus:text-slate-900")}>
          <HelpCircle className="w-4 h-4 mr-2" />
          Help & Support
        </DropdownMenuItem>
        <DropdownMenuSeparator className={isBrandTheme || isDark ? "bg-white/10" : "bg-slate-100"} />
        <DropdownMenuItem onClick={handlePrimary} className={cn("cursor-pointer", isBrandTheme || isDark ? "focus:bg-white/10 focus:text-white" : "focus:bg-slate-100 focus:text-slate-900")}>
          <PrimaryIcon className="w-4 h-4 mr-2" />
          {primaryActionLabel}
          </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-2 p-2">
        <Avatar className={cn("h-10 w-10 border", isBrandTheme ? "border-white/10" : (isDark ? "border-gray-700" : "border-slate-200"))}>
          {user?.image ? <AvatarImage src={user.image} alt={displayName} /> : null}
          <AvatarFallback
            className="text-white"
            style={{ background: accentGradient, color: accentTextColor }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {menu}
      </div>
    );
  }

  return (
    <div className={cn(
      "rounded-2xl border p-4 backdrop-blur transition-colors",
      isBrandTheme 
        ? "border-white/10 bg-white/5" 
        : (isDark 
          ? "border-gray-800 bg-gray-800/50" 
          : "border-slate-200 bg-slate-50/50")
    )}>
      <div className="flex items-center gap-3">
        <Avatar className={cn("h-12 w-12 border", isBrandTheme ? "border-white/10" : (isDark ? "border-gray-700" : "border-slate-200"))}>
          {user?.image ? <AvatarImage src={user.image} alt={displayName} /> : null}
          <AvatarFallback
            className={cn("text-sm", isBrandTheme || isDark ? "text-white" : "text-white")} // text-white always visible on gradient? Yes.
            style={{ background: accentGradient, color: accentTextColor }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-semibold truncate", isBrandTheme || isDark ? "text-white" : "text-slate-900")}>{displayName}</p>
          <p className={cn("text-xs truncate", isBrandTheme || isDark ? "text-white/70" : "text-slate-500")}>{displayAccount}</p>
        </div>
        {menu}
      </div>
      {!isAuthenticated && (
        <>
          <Button
            variant="ghost"
            className={cn(
              "mt-4 w-full justify-center rounded-xl border transition-colors",
              isBrandTheme 
                ? "border-white/10 bg-white/10 text-white hover:bg-white/20" 
                : (isDark 
                  ? "border-gray-700 bg-gray-800 text-white hover:bg-gray-700" 
                  : "border-slate-200 bg-white text-slate-900 hover:bg-slate-100")
            )}
            onClick={handlePrimary}
          >
            <PrimaryIcon className="w-4 h-4 mr-2" />
            Sign in
          </Button>
          <p className={cn("mt-2 text-[11px] text-center", isBrandTheme || isDark ? "text-white/70" : "text-slate-500")}>
            Use your NodeRiver credentials to unlock split view and conversation history.
          </p>
        </>
      )}
    </div>
  );
};

export default AccountMenu;

